// Roles and permissions (docs/cockpit/spec.md): what each Role may see, do and approve across the business,
// the Sensitive Data Classes each sees, and who approves what. Changing it is a Tree Change: by chat ("let
// Floor Supervisor see Goods Receipt") or by showing or hiding a kind of data here, previewed before an Owner
// confirms it.
import { useCallback, useEffect, useState } from "react";
import {
  ApiError,
  type Access,
  type Api,
  type Roles as RolesData,
  type SensitiveDataClass,
  type TreeChange,
} from "../api.js";
import { useWords, type Words } from "../i18n/words.js";
import { ChangePreview, appliedNotice } from "../settings/ChangePreview.js";

const problemOf = (error: unknown, w: Words) =>
  error instanceof ApiError
    ? error.problems.length > 0
      ? error.problems.join("; ")
      : w.ownerPagesRefused(error.code)
    : w.ownerPagesUnreachable;

/** The words of each kind of access, as a catalogue key. */
const ACCESS = {
  approve: "rolesApprove",
  act: "rolesAct",
  see: "rolesSee",
} as const satisfies Record<Exclude<Access, null>, keyof Words>;
/** The name of each Sensitive Data Class, as a catalogue key. */
const DATA_CLASS_NAMES = {
  costs_and_margins: "rolesCostsAndMargins",
  payroll: "rolesPayroll",
  bank_and_cash: "rolesBankAndCash",
  customer_pricing: "rolesCustomerPricing",
} as const satisfies Record<SensitiveDataClass, keyof Words>;
const OWNER = "owner";

export function Roles({ api }: { api: Api }) {
  const w = useWords();
  const [roles, setRoles] = useState<RolesData | undefined>(undefined);
  const [preview, setPreview] = useState<TreeChange | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.roles().then(setRoles, (error: unknown) => setProblem(problemOf(error, w)));
  }, [api]);
  useEffect(load, [load]);

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setNotice(undefined);
    setProblem(undefined);
    try {
      await work();
    } catch (error) {
      setProblem(problemOf(error, w));
    } finally {
      setBusy(false);
    }
  };

  if (!roles) return problem ? <p role="alert">{problem}</p> : <p role="status">{w.rolesLoading}</p>;

  const toggle = (role: string, dataClass: SensitiveDataClass, sees: boolean) =>
    run(async () => {
      const now = roles.sensitiveData
        .filter((d) => (d.dataClass === dataClass ? !sees : d.roles[role] === true))
        .map((d) => d.dataClass);
      setPreview(await api.proposeRoleData(role, now));
    });
  const confirm = (change: TreeChange, migrate: boolean) =>
    run(async () => {
      setNotice(appliedNotice(await api.confirmChange(change.id, migrate), w));
      setPreview(undefined);
      load();
    });

  const approvals = roles.roles.flatMap((r) => r.approves.map((what) => ({ what, role: r.name })));
  return (
    <>
      <p className="muted">{w.rolesIntro}</p>
      {notice ? <p role="status">{notice}</p> : null}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
      {preview ? (
        <ChangePreview
          change={preview}
          owner
          busy={busy}
          onConfirm={(c, m) => void confirm(c, m)}
          onDismiss={() => setPreview(undefined)}
        />
      ) : null}
      <div className="tbl-wrap">
        <table className="tbl" aria-label={w.rolesWhatEachMayDo}>
          <thead>
            <tr>
              <th scope="col">{w.rolesArea}</th>
              {roles.roles.map((r) => (
                <th key={r.id} scope="col">
                  {r.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {roles.areas.map((area) => (
              <tr key={area.node}>
                <th scope="row">
                  {area.name} <span className="small muted">{area.node}</span>
                </th>
                {roles.roles.map((r) => {
                  const access = area.access[r.id] ?? null;
                  return <td key={r.id}>{access ? w[ACCESS[access]] : "—"}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h2 className="sectionh">{w.rolesSensitiveData}</h2>
      <div className="tbl-wrap">
        <table className="tbl" aria-label={w.rolesSensitiveDataEach}>
          <thead>
            <tr>
              <th scope="col">{w.rolesKindOfData}</th>
              {roles.roles.map((r) => (
                <th key={r.id} scope="col">
                  {r.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {roles.sensitiveData.map((d) => (
              <tr key={d.dataClass}>
                <th scope="row">{w[DATA_CLASS_NAMES[d.dataClass]]}</th>
                {roles.roles.map((r) => {
                  const sees = d.roles[r.id] === true;
                  const data = w[DATA_CLASS_NAMES[d.dataClass]];
                  return (
                    <td key={r.id}>
                      {sees ? w.rolesYes : w.rolesNo}
                      {r.id === OWNER ? null : (
                        <>
                          {" "}
                          <button
                            type="button"
                            className="btn btn-small"
                            disabled={busy}
                            aria-label={sees ? w.rolesHideFrom(data, r.name) : w.rolesShowTo(data, r.name)}
                            onClick={() => void toggle(r.id, d.dataClass, sees)}
                          >
                            {sees ? w.rolesHide : w.rolesShow}
                          </button>
                        </>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h2 className="sectionh">{w.rolesWhoApproves}</h2>
      <div className="tbl-wrap">
        <table className="tbl" aria-label={w.rolesWhoApproves}>
          <thead>
            <tr>
              <th scope="col">{w.rolesWhen}</th>
              <th scope="col">{w.rolesGoesTo}</th>
            </tr>
          </thead>
          <tbody>
            {approvals.map((a) => (
              <tr key={`${a.role}:${a.what}`}>
                <td>{a.what}</td>
                <td>{a.role}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="small muted">{w.rolesVersion(roles.version)}</p>
    </>
  );
}
